import styled from "styled-components";
import Select from "react-select";
import { useEffect, useState } from "react";
import { colors } from "styles/global-styles";
import makeAnimated from 'react-select/animated';

// input[checkbox]
export const CustomCheckbox = styled.input`
  &[type="checkbox"] {
    appearance: none;
    width: 1.25rem;
    min-width: 1.25rem;
    height: 1.25rem;
    cursor: pointer;
    display: block;
    border: 1px solid ${({theme}) => theme.newTheme.border.primaryBd};
    border-radius: 0.25rem;
    margin: 0;

    &:checked {
      background-color: ${({theme}) => theme.newTheme.default.accent};
      border-color: ${({theme}) => theme.newTheme.default.accent};
    }
  }
`;

interface SwitchContainerProps {
  checked: boolean;
}

export const SwitchContainer = styled.div<SwitchContainerProps>`
  position: relative;
  display: inline-block;
  width: 55px;
  height: 30px;
  background-color: ${(props) =>
    props.checked ? props.theme.newTheme.default.accent : '#ccc'};
  border-radius: 30px;
  cursor: pointer;
  transition: 0.4s;

  &:before {
    position: absolute;
    content: "";
    height: 22px;
    width: 22px;
    left: ${(props) => (props.checked ? '27px' : '4px')};
    bottom: 4px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

export const CommonInput = styled.input`
  font-size: 0.875rem;
  height: 2.75rem;
  background-color: ${({ theme }) => theme.newTheme.textField.defaultBg};
  border: 1px solid ${({theme}) => theme.newTheme.textField.defaultBg};
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;

  &:focus,
  &:focus-visible {
    outline: none;
  }
  &:not(:disabled, :read-only):focus,
  &:not(:disabled, :read-only):focus-visible {
    background-color: ${({ theme }) => theme.newTheme.textField.activeBg};
    border: 1px solid ${({ theme }) => theme.newTheme.default.primary};
  }
  &:disabled, &:read-only {
    color: ${({ theme }) => theme.newTheme.textField.disabledText};
    cursor: default;
  }
  &:disabled {
    background-color: ${({ theme }) => theme.newTheme.textField.disabledBg};
  }
`;

export type selectOption = {
  value: string | number;
  label: string;
};

const customStyles = {
  control: (styles: any, state: any) => ({
    ...styles,
    border: "none",
    backgroundColor: state.isDisabled ? colors.textFieldDisabledBg : colors.textFieldDefaultBg,
    boxShadow: "none",
    ":hover": {
      borderColor: colors.primaryHover,
      boxShadow: `0 0 0 1px ${colors.primary}`,
      svg: { fill: colors.primary },
    },
    ":active": { borderColor: colors.primaryHover },
    ":focus": { borderColor: colors.primaryHover },
    ":focus-visible": { borderColor: colors.primaryHover },
    "> div:first-of-type": {
      minWidth: '5rem',
      paddingLeft: "1rem",
      paddingRight: "0.5rem"
    },
    "> div": {
      padding: 0,
    }
  }),
  indicatorSeparator: (styles: any) => ({
    ...styles,
    ":hover": {
      backgroundColor: colors.primary,
    },
  }),
  indicatorsContainer: (styles: any) => ({
    ...styles,
    color: colors.primary,
    backgroundColor: colors.secondaryBd,
    borderRadius: '0.25rem',
  }),
  menu: (styles: any) => ({
    ...styles,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: "200px",
  }),
  menuPortal: (base:any) => ({ ...base, zIndex: 25 }), // 드롭다운 메뉴를 포털로 렌더링할 수 있는 menuPortalTarget 속성 제공(overflow 속성의 영향 받지않게 함)
  option: (styles: any) => ({
    ...styles,
    ":hover": { backgroundColor: colors.secondaryHover },
  }),
  valueContainer: (styles: any) => ({
    ...styles,
    top: 0,
    transform: 'none',
    lineHeight: '2rem'
  }),
  singleValue: (styles: any) => ({
    ...styles,
    top: 0,
    transform: 'none',
    lineHeight: '2rem'
  })
  
};

// select-option
export function SelectOption({
  optionData,
  onChange,
  defaultValue,
  dontChange,
  placeholderText,
  searchable,
}: {
  optionData: selectOption[];
  onChange: Function;
  dontChange?: boolean;
  defaultValue?: number;
  placeholderText?: string;
  searchable?: boolean;
  }) {
  const [selectOption, setSelectOption] = useState(0);
  function changeSelectOption(event: any) {
    const value = event === null ? 0 : event.value;
    setSelectOption(value);
    onChange(value);
  }
  useEffect(() => {
    // 기본값이 있는 경우
    if (defaultValue) {
      if (optionData.some(o => o.value === defaultValue))
        setSelectOption(defaultValue);
      else setSelectOption(0); // 기본값이 전달해준 옵션에 없는 값이면 0
    }
    else setSelectOption(0);
  }, [optionData, defaultValue]);
  
  return (
    <Select
      styles={customStyles}
      options={optionData}
      value={optionData.filter((o) => o.value === selectOption)}
      // defaultValue={defaultValue ? defaultValue : optionData[0]}
      onChange={(e: any) => changeSelectOption(e)}
      isSearchable={searchable || false}
      isClearable
      isDisabled={dontChange}
      placeholder={placeholderText || "선택해주세요"}
      menuPortalTarget={document.body}
      menuPlacement='auto'
      theme={(theme: any) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: colors.primary,
          primary25: colors.secondaryHover
        }
      })}
    />
  );
}

// 출결용 select-option
export function AttendanceStatusSelectOption({
  optionData,
  onChange,
  defaultValue,
  onBlur,
  name,
}: {
  optionData: selectOption[];
  onChange: Function;
  defaultValue?: number;
  onBlur?: Function;
  name?: string
}) {
  const [selectOption, setSelectOption] = useState(0);
  function changeSelectOption(event: any) {
    // console.log("event ", event);
    const value = event === null ? 0 : event.value;
    setSelectOption(value);
    onChange(value);
  }
  function blurSelectOption() {
    if (selectOption !== 2) onChange(selectOption);
  }
  useEffect(() => {
    if (defaultValue) setSelectOption(defaultValue);
    else setSelectOption(0);
  }, [optionData, defaultValue]);
  return (
    <Select
      name={name}
      styles={customStyles}
      options={optionData}
      value={optionData.filter((o) => o.value === selectOption)}
      onChange={(e: any) => changeSelectOption(e)}
      onBlur={blurSelectOption}
      isSearchable={false}
      menuPlacement='auto'
    />
  );
}

// multiple select-option
export function MultipleSelectOption({ optionData, onChange, placeholderText }: {
  optionData: selectOption[]; onChange: Function; placeholderText?: string;
}) {
  // console.log("multi optionData ", optionData);
  const animatedComponents = makeAnimated();
  const [selectOption, setSelectOption] = useState([optionData[0]]);

  function changeSelectOption(option: any) {
    let newOption = option;
    // 기존 값에 '전체'가 포함되어 있는 경우
    if (!selectOption.some((v: any) => v === undefined) && selectOption.some((v: any) => v.value === 0)) {
      newOption = newOption.filter((o: any) => o.value !== 0);
      setSelectOption(newOption);
    }
    else {
      // 선택한 값에 '전체'가 포함된 경우
      if (option.some((v: any) => v.value === 0)) {
        newOption = newOption.filter((o:any) => o.value === 0);
        setSelectOption(newOption);
      }
      // 선택한 값이 '전체'와 동일한 경우
      else if (option.length === optionData.length - 1) setSelectOption([optionData[0]]);
      // 선택한 값에 '전체'가 포함이 안된 경우
      else setSelectOption(newOption);
    }
    onChange(newOption);
  }
  return (
    <Select
      closeMenuOnSelect={false}
      styles={customStyles}
      options={optionData}
      value={selectOption}
      isClearable={false}
      // onChange={(e: any) => changeSelectOption(e.value)}
      onChange={(e: any) => changeSelectOption(e)}
      isSearchable={false}
      isMulti
      components={animatedComponents}
      placeholder={placeholderText || "선택해주세요"}
      menuPlacement='auto'
    />
  );
}
